/**
 * App Component - Main Application Entry
 */

import React from "react";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import { router } from "./router";
import { getTheme } from "./theme";
import { useUIStore } from "@/store/uiStore";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { AppToaster } from "@/components/common/AppToaster";
import { AuthSyncProvider } from "@/components/auth/AuthSyncProvider";

import { queryClient } from "@/api/queryClient";

const App: React.FC = () => {
  // Selector subscription: re-render App only when `theme` changes, not on
  // every unrelated UI-store mutation (sidebar/campus/section toggles).
  const theme = useUIStore((s) => s.theme);
  const muiTheme = React.useMemo(() => getTheme(theme), [theme]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <GlobalStyles styles={{
            ".sr-only": {
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              borderWidth: 0,
            },
            // Respect the OS "reduce motion" preference (WCAG 2.3.3): neutralize
            // hover transforms, transitions, and animations app-wide.
            "@media (prefers-reduced-motion: reduce)": {
              "*, *::before, *::after": {
                animationDuration: "0.01ms !important",
                animationIterationCount: "1 !important",
                transitionDuration: "0.01ms !important",
                scrollBehavior: "auto !important",
              },
            },
          }} />
          <AuthSyncProvider>
            <RouterProvider router={router} />
          </AuthSyncProvider>
          <AppToaster theme={theme} />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
